"use client";

import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { usePathname, useRouter } from "next/navigation";
import useTranslations from "../hooks/useTranslation";
import { getDictionary, getField } from "../utils/translation/translation";
import type { ReactNode } from "react";

// Theme Context
type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
    mode: ThemeMode;
    toggleTheme: () => void;
    setTheme: (mode: ThemeMode) => void;
}

const defaultThemeContext: ThemeContextType = {
    mode: 'light',
    toggleTheme: () => {},
    setTheme: () => {},
};

export const ThemeContext = createContext<ThemeContextType>(defaultThemeContext);

export function useTheme(): ThemeContextType {
    return useContext(ThemeContext);
}

export type { ThemeMode };

function getLangCookie(): string | null {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp('(^| )lang=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
}

export default function LayoutManager({ children }: { children?: ReactNode }) {
    const pathname = usePathname();
    const router = useRouter();

    // Theme state
    const [themeMode, setThemeMode] = useState<ThemeMode>('light');
    const [themeMounted, setThemeMounted] = useState(false);

    // Function to set CSS variables based on theme
    const setThemeVariables = useCallback((mode: ThemeMode) => {
        if (typeof window === 'undefined') return;
        
        const root = document.documentElement;
        
        // Set data-theme attribute for CSS fallback
        root.setAttribute('data-theme', mode);
        
        if (mode === 'dark') {
            // Dark mode variables
            root.style.setProperty('--bg-primary', '#12161A');
            root.style.setProperty('--bg-secondary', '#1E2630');
            root.style.setProperty('--bg-tertiary', '#1E2630');
            root.style.setProperty('--section-bg', '#222B36');
            root.style.setProperty('--section-bg-alt', '#1A2028');
            root.style.setProperty('--section-bg-hover', '#2A3441');
            root.style.setProperty('--text-primary', '#919EAB');
            root.style.setProperty('--text-secondary', '#B0B0B0');
            root.style.setProperty('--text-tertiary', '#808080');
            root.style.setProperty('--text-inverse', '#222B36');
            root.style.setProperty('--border-color', 'rgba(255, 255, 255, 0.2)');
            root.style.setProperty('--border-color-hover', 'rgba(255, 255, 255, 0.4)');
            root.style.setProperty('--accent-primary', '#9463A4');
            root.style.setProperty('--accent-secondary', '#FDC700');
            root.style.setProperty('--accent-hover', '#9B4DD9');
        } else {
            // Light mode variables
            root.style.setProperty('--bg-primary', '#FAFAFA');
            // root.style.setProperty('--bg-primary', '#F0F0F0'); // Increased contrast version (commented for future use)
            root.style.setProperty('--bg-secondary', '#FFFFFF');
            root.style.setProperty('--bg-tertiary', '#F8F6F9');
            root.style.setProperty('--section-bg', '#FFFFFF');
            root.style.setProperty('--section-bg-alt', '#F8F6F9');
            // root.style.setProperty('--section-bg-alt', '#F5F5F5'); // Increased contrast version (commented for future use)
            root.style.setProperty('--section-bg-hover', '#F0F0F0');
            // root.style.setProperty('--section-bg-hover', '#E8E8E8'); // Increased contrast version (commented for future use)
            root.style.setProperty('--text-primary', '#4A4848');
            root.style.setProperty('--text-secondary', '#666666');
            root.style.setProperty('--text-tertiary', '#999999');
            root.style.setProperty('--text-inverse', '#FFFFFF');
            root.style.setProperty('--border-color', 'rgba(74, 72, 72, 0.2)');
            root.style.setProperty('--border-color-hover', 'rgba(74, 72, 72, 0.4)');
            root.style.setProperty('--accent-primary', '#8130CB');
            root.style.setProperty('--accent-secondary', '#FED402');
            root.style.setProperty('--accent-hover', '#6B28A8');
        }
    }, []);

    // Initialize theme
    useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            const savedTheme = localStorage.getItem('theme') as ThemeMode;
            let initialTheme: ThemeMode = 'light';
            
            if (savedTheme === 'dark' || savedTheme === 'light') {
                initialTheme = savedTheme;
            } else {
                // Check system preference
                const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                initialTheme = prefersDark ? 'dark' : 'light';
            }
            
            setThemeMode(initialTheme);
            setThemeVariables(initialTheme);
            localStorage.setItem('theme', initialTheme); // Ensure localStorage is set on initial load
        } catch (error) {
            console.warn('Failed to access localStorage:', error);
            setThemeVariables('light'); // Fallback
            localStorage.setItem('theme', 'light'); // Fallback
        } finally {
            setThemeMounted(true);
        }
    }, [setThemeVariables]);

    // Sync theme changes
    useEffect(() => {
        if (!themeMounted || typeof window === 'undefined') return;
        try {
            setThemeVariables(themeMode);
            localStorage.setItem('theme', themeMode);
        } catch (error) {
            console.warn('Failed to update theme:', error);
        }
    }, [themeMode, themeMounted, setThemeVariables]);

    const toggleTheme = useCallback(() => {
        setThemeMode((prev) => (prev === 'light' ? 'dark' : 'light'));
    }, []);

    const setTheme = useCallback((newMode: ThemeMode) => {
        setThemeMode(newMode);
    }, []);

    const {
        loading,
        translations,
        supportedLanguages,
        language,
        selectedLanguage,
        handleLanguageChange,
        setLanguage
    } = useTranslations();



    const [cookieLang, setCookieLang] = useState(getLangCookie());

    useEffect(() => {
        const interval = setInterval(() => {
            const currentLang = getLangCookie();
            if (currentLang !== cookieLang) {
                setCookieLang(currentLang);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [cookieLang]);

    const effectiveLang = cookieLang || language;


    useEffect(() => {
        const SUPPORTED_LANG_CODES = ['en', 'fr', 'ar'];
        const segments = pathname?.split('/').filter(Boolean);
        const langCode = segments[0];
        if (langCode && SUPPORTED_LANG_CODES.includes(langCode)) {
            const langName = langCode === 'ar' ? 'arabic' : langCode === 'fr' ? 'french' : 'english';
            document.cookie = `lang=${langName}; path=/; max-age=${60 * 60 * 24 * 30}`;
            if (language !== langName) {
                setLanguage(langName);
            }
            return;
        }
        if (!langCode) {
            document.cookie = `lang=english; path=/; max-age=${60 * 60 * 24 * 30}`;
            if (language !== 'english') {
                setLanguage('english');
            }
            return;
        }
        const match = document.cookie.match(/(^| )lang=([^;]+)/);
        const cookieLang = match ? decodeURIComponent(match[2]) : null;
        if (cookieLang && language !== cookieLang) {
            setLanguage(cookieLang);
        }
    }, [pathname, language, setLanguage]);

    useEffect(() => {
        if (!pathname || loading || translations.length === 0) return;
        const dictionary = getDictionary(translations, 'layout', effectiveLang);
        const pathKeys: { [key: string]: { titleKey: string; descriptionKey: string } } = {
            '/': { titleKey: 'title1', descriptionKey: 'desc1' },
            '/qr-digital-menu': { titleKey: 'title2', descriptionKey: 'desc2' },
            '/online-ordering': { titleKey: 'title3', descriptionKey: 'desc3' },
            '/crm-marketing': { titleKey: 'title4', descriptionKey: 'desc4' },
            '/branded-apps': { titleKey: 'title5', descriptionKey: 'desc5' },
            '/loyalty': { titleKey: 'title6', descriptionKey: 'desc6' },
            '/qr-ordering': { titleKey: 'title7', descriptionKey: 'desc7' },
            '/terms': { titleKey: 'title8', descriptionKey: 'desc8' },
            '/privacy-policy': { titleKey: 'title9', descriptionKey: 'desc9' },
            '/about-us': { titleKey: 'title10', descriptionKey: 'desc10' },
            '/blogs': { titleKey: 'title11', descriptionKey: 'desc11' },
            '/seo': { titleKey: 'title16', descriptionKey: 'desc16' },
            '/thankyou': { titleKey: 'title17', descriptionKey: 'desc17' },
            '/blogs/efficient-ordering': { titleKey: 'title12', descriptionKey: 'desc12' },
            '/blogs/reduce-error': { titleKey: 'title13', descriptionKey: 'desc13' },
            '/blogs/transform-your-restaurant': { titleKey: 'title14', descriptionKey: 'desc14' },
            '/blogs/digital-ordering': { titleKey: 'title15', descriptionKey: 'desc15' },
        };

        const pathKey = pathname.replace(/^\/?(en|ar|fr)/, '').replace(/\/$/, '') || '/';

        const keys = pathKeys[pathKey] || { titleKey: 'defaultTitle', descriptionKey: 'defaultDescription' };

        const title = getField(dictionary, keys.titleKey, effectiveLang) || 'Cloudix - Restaurant Ordering System & CRM';
        const description = getField(dictionary, keys.descriptionKey, effectiveLang) || 'Cloudix. Restaurant Ordering system, CRM and Marketing, all in one easy-to-use app';

        // Only update title if it's empty or default, to avoid conflicts with server-side metadata
        if (!document.title || document.title.trim() === '' || document.title === 'Cloudix') {
            document.title = title;
        }

        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription && (!metaDescription.getAttribute('content') || metaDescription.getAttribute('content')?.trim() === '')) {
            metaDescription.setAttribute('content', description);
        }
        
        // Canonical and hreflang links are now handled by server-side metadata
        // via PageMetadata utility to avoid conflicts and duplicates
        // Additional safeguard: Ensure no client-side canonical modifications occur
        const existingCanonical = document.querySelector('link[rel="canonical"]');
        if (!existingCanonical) {
            console.warn('No server-side canonical URL found for path:', pathname);
        }
    }, [pathname, translations, effectiveLang, loading]);

    const themeValue: ThemeContextType = useMemo(() => ({
        mode: themeMode,
        toggleTheme,
        setTheme,
    }), [themeMode, toggleTheme, setTheme]);

    if (children) {
        return (
            <ThemeContext.Provider value={themeValue}>
                {children}
            </ThemeContext.Provider>
        );
    }

    return null;
}

